import { Box, Typography } from "@mui/material";
import RedirectBackButton from "components/RedirectBackButton";
import { FORM_ERROR } from "final-form";
import useGlobalContext from "hooks/globalData";
import useTitle from "hooks/useTitle";
import useTrashablePromises from "hooks/useTrashablePromises";
import { kebabCase } from "lodash";
import { useIntl } from "react-intl";
import { useQueryClient } from "react-query";
import { Navigate, useNavigate } from "react-router-dom";
import {
  SaveAttributeRequest,
  SaveResponse,
} from "services/KnowfullyAdminClient";
import attributeStore from "stores/attributeStore";
import translateError from "utils/translateError";
import { AttributeForm } from "./AttributeForm";

export function AttributeCreateForm() {
  const { registerPromise } = useTrashablePromises();
  const { formatMessage: t } = useIntl();
  const {
    attributeService,
    attributeDetailsUrl,
    attributeListUrl,
    attributesUrl,
  } = useGlobalContext();
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const organizationAttributeType = attributeStore.useState(
    (s) => s.organizationAttributeType
  );

  const title = t(
    { id: "createAttributeLabel" },
    { attributeType: organizationAttributeType?.attributeType?.name ?? "" }
  );
  useTitle(title);

  if (!organizationAttributeType) return <Navigate to={attributesUrl()} replace />;

  const codeConstant =
    organizationAttributeType.attributeType?.codeConstant ?? "";

  const backToListUrl = attributeListUrl(kebabCase(codeConstant));

  const onCancel = () => {
    navigate(backToListUrl);
  };

  const newAttributeTitle = t(
    { id: "newLabel" },
    { name: organizationAttributeType.attributeType?.name }
  );

  const onSubmit = async (values: SaveAttributeRequest) => {
    try {
      const createdAttribute: SaveResponse = await registerPromise(
        attributeService.saveAttribute(
          SaveAttributeRequest.fromJS({
            attributeTypeId: organizationAttributeType?.attributeTypeId,
            organizationAttributeTypeId: organizationAttributeType?.id,
            displayText: values.displayText || values.value,
            ...values,
          })
        )
      );

      queryClient.invalidateQueries(["getAttributes"]);
      queryClient.invalidateQueries(["skuAttributesByType"]);
      queryClient.invalidateQueries(["attributes"]);

      navigate(attributeDetailsUrl(createdAttribute.id));

      return createdAttribute;
    } catch (err: any) {
      return { [FORM_ERROR]: translateError(err, t) };
    }
  };

  return (
    <>
      <Box my={2}>
        <RedirectBackButton
          redirectRoute={backToListUrl}
          labelKey="backToListButtonLabel"
        />
      </Box>
      <Typography variant="h5">{newAttributeTitle}</Typography>
      <AttributeForm
        onSubmit={onSubmit}
        organizationAttributeType={organizationAttributeType}
        submitLabel={title}
        onCancel={onCancel}
      />
    </>
  );
}
